/**
 * 問題文・選択肢などのテキスト表示用ユーティリティ
 *
 * レガシーデータでは HTML が base64 エンコードされている場合がある。
 * 英数字のみのプレーンテキストを誤って base64 デコードしないよう、
 * デコード結果が HTML である場合にのみデコードする。
 */

/** HTMLタグを含むかどうかを判定 */
export const looksLikeHtml = (text: string): boolean => {
  return (
    /<[a-zA-Z][a-zA-Z0-9]*[\s>/]/.test(text) ||
    /<\/[a-zA-Z][a-zA-Z0-9]*>/.test(text)
  );
};

/** base64 文字列の形式かどうかを判定 */
const looksLikeBase64String = (text: string): boolean => {
  const trimmed = text.trim();
  if (trimmed.length < 4 || trimmed.length % 4 !== 0) {
    return false;
  }
  return /^[A-Za-z0-9+/]+=*$/.test(trimmed);
};

/** UTF-8 バイト列を base64 デコード */
const decodeBase64ToUtf8 = (base64: string): string => {
  const binary = atob(base64.trim());
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

/** 非 ASCII 文字（日本語等）を含むかどうかを判定 */
const hasNonAscii = (text: string): boolean => {
  for (const char of text) {
    if (char.charCodeAt(0) > 127) {
      return true;
    }
  }
  return false;
};

/**
 * base64 エンコードされた HTML をデコードする。
 * プレーンテキスト（英数字・日本語・HTML）はそのまま返す。
 */
export const decodeQuestionText = (text: string): string => {
  if (!text) {
    return text;
  }

  // data URI 形式はそのまま返す
  if (text.startsWith('data:image') || text.includes('base64,')) {
    return text;
  }

  // 既に HTML または非 ASCII（日本語等）のプレーンテキスト
  if (looksLikeHtml(text) || hasNonAscii(text)) {
    return text;
  }

  // 空白・句読点を含む場合はプレーンテキストとして扱う
  if (/[\s,.:;!?'"()-]/.test(text)) {
    return text;
  }

  if (!looksLikeBase64String(text)) {
    return text;
  }

  try {
    const decoded = decodeBase64ToUtf8(text);
    // デコード結果が HTML の場合のみ適用（英数字のみの誤デコードを防止）
    if (looksLikeHtml(decoded)) {
      return decoded;
    }
    return text;
  } catch {
    return text;
  }
};

/** HTMLタグを除去してプレーンテキストに変換 */
export const stripHtmlTags = (html: string): string => {
  try {
    const decoded = decodeQuestionText(html);
    return decoded.replace(/<[^>]*>/g, '').trim();
  } catch {
    return html.replace(/<[^>]*>/g, '').trim();
  }
};
